if (!customElements.get('xoa-nail-measurement')) {
  customElements.define(
    'xoa-nail-measurement',
    class XoaNailMeasurement extends HTMLElement {
      constructor() {
        super();
        this.fingers = ['thumb', 'index', 'middle', 'ring', 'little'];
        this.hands = ['left', 'right'];
        this.stepNames = ['Mittausohje', 'Vasen käsi', 'Oikea käsi', 'Tarkista mitat'];
        this.values = { left: {}, right: {} };
        this.shape = null;
        this.recommendation = null;
        // XOA:n julkaistut press-on-kokotaulukot: tippinumerot 0–11 ja niiden leveydet millimetreinä.
        this.charts = {
          medium_coffin: {
            label: 'Medium Coffin',
            widths: [17, 16, 15, 14.5, 14, 13, 12, 11, 10, 9, 8, 7],
            variantAliases: ['mediumcoffin'],
          },
          long_coffin: {
            label: 'Long Coffin',
            widths: [17, 16, 15.5, 15, 14, 13, 13, 12.5, 12, 11.5, 10, 9],
            variantAliases: ['longcoffin'],
          },
          medium_almond: {
            label: 'Almond',
            widths: [17, 16, 15, 14, 13, 12.5, 12, 11.5, 11, 10, 9, 7],
            variantAliases: ['almond', 'mediumalmond'],
          },
          medium_stiletto: {
            label: 'Medium Stiletto',
            widths: [16.5, 16, 15, 14, 13, 12, 11, 10, 10, 9, 8, 7],
            variantAliases: ['mediumstiletto'],
          },
          long_stiletto: {
            label: 'Long Stiletto',
            widths: [17, 16.5, 16, 15, 14.5, 13.5, 13, 12, 11.5, 10.5, 9, 8],
            variantAliases: ['longstiletto'],
          },
          medium_square: {
            label: 'Medium Square',
            widths: [16, 15, 14, 13, 12, 11.5, 11, 11, 10, 9, 8, 7],
            variantAliases: ['mediumsquare'],
          },
        };
        this.legacyShapes = { coffin: 'long_coffin', stiletto: 'long_stiletto' };
        this.guideViews = {
          1: {
            title: 'Vaihe 1 · Aseta teippi',
            viewBox: '135 251 381 224',
            image: 'overview',
            imageWidth: 1080,
            imageHeight: 1080,
            caption: 'Aseta teippi kynnen leveimpään kohtaan ja merkitse kynnen molemmat reunat.',
          },
          2: {
            title: 'Vaihe 2 · Mittaa teippi',
            viewBox: '565 251 380 224',
            image: 'overview',
            imageWidth: 1080,
            imageHeight: 1080,
            caption: 'Aseta teippi tasaiselle pinnalle ja mittaa merkkien väli millimetreinä.',
          },
          3: {
            title: 'Vaihe 3 · Kirjaa mitat',
            viewBox: '135 645 381 222',
            image: 'overview',
            imageWidth: 1080,
            imageHeight: 1080,
            caption: 'Kirjaa jokaisen kynnen leveys ylös peukalosta pikkurilliin.',
          },
          4: {
            title: 'Vaihe 4 · Valitse koko',
            viewBox: '0 0 2000 1600',
            image: 'size',
            imageWidth: 2000,
            imageHeight: 1600,
            caption: 'Kokoapu vertaa mittoja valitsemasi muodon taulukkoon ja suosittelee kokoa.',
          },
        };
        this.sizeSets = {
          XS: [6, 9, 7, 8, 11],
          S: [4, 7, 5, 6, 10],
          M: [2, 7, 5, 6, 9],
          L: [1, 6, 3, 4, 7],
          XL: [0, 4, 2, 3, 6],
        };
        this.currentStep = 0;
        this.saved = false;
      }

      connectedCallback() {
        this.dialog = this.querySelector('[data-measurement-dialog]');
        this.viewport = this.querySelector('[data-measurement-viewport]');
        this.openButton = this.querySelector('[data-measurement-open]');
        this.closeButton = this.querySelector('[data-measurement-close]');
        this.backButton = this.querySelector('[data-measurement-back]');
        this.nextButton = this.querySelector('[data-measurement-next]');
        this.saveButton = this.querySelector('[data-measurement-save]');
        this.copyButton = this.querySelector('[data-measurement-copy]');
        this.clearButton = this.querySelector('[data-measurement-clear]');
        this.savedMessage = this.querySelector('[data-measurement-saved]');
        this.savedTitle = this.querySelector('[data-measurement-saved-title]');
        this.savedHelp = this.querySelector('[data-measurement-saved-help]');
        this.progressLabel = this.querySelector('[data-progress-label]');
        this.progressName = this.querySelector('[data-progress-name]');
        this.progressTrack = this.querySelector('[data-progress-track]');
        this.progressBar = this.querySelector('[data-progress-bar]');
        this.announcement = this.querySelector('[data-measurement-announcement]');
        this.shapeInputs = Array.from(this.querySelectorAll('[data-shape-input]'));
        this.shapeError = this.querySelector('[data-shape-error]');
        this.recommendedSize = this.querySelector('[data-recommended-size]');
        this.recommendedShape = this.querySelector('[data-recommended-shape]');
        this.recommendationCopy = this.querySelector('[data-recommendation-copy]');
        this.recommendationNote = this.querySelector('[data-recommendation-note]');
        this.cartFlowSaveTitle = this.querySelector('[data-cart-flow-save-title]');
        this.cartFlowSaveCopy = this.querySelector('[data-cart-flow-save-copy]');
        this.cartFlowCartCopy = this.querySelector('[data-cart-flow-cart-copy]');
        this.guideDialog = this.querySelector('[data-guide-dialog]');
        this.guideImage = this.querySelector('[data-guide-image]');
        this.guideImageSource = this.querySelector('[data-guide-image-source]');
        this.guideTitle = this.querySelector('[data-guide-title]');
        this.guideCaption = this.querySelector('[data-guide-caption]');
        this.guideCloseButton = this.querySelector('[data-guide-close]');
        this.minimum = Number(this.dataset.minimum) || 5;
        this.maximum = Number(this.dataset.maximum) || 25;
        this.storageKey = `xoa-nail-measurement:${this.dataset.productId}`;

        if (!this.dialog || !this.openButton) return;

        this.handleOpen = this.open.bind(this);
        this.handleClose = this.close.bind(this);
        this.handleDialogClick = this.onDialogClick.bind(this);
        this.handleDialogClose = this.onDialogClose.bind(this);
        this.handleBack = this.back.bind(this);
        this.handleNext = this.next.bind(this);
        this.handleSave = this.save.bind(this);
        this.handleCopy = this.copy.bind(this);
        this.handleClear = this.clear.bind(this);
        this.handleInput = this.onInput.bind(this);
        this.handleShapeChange = this.onShapeChange.bind(this);
        this.handleKeydown = this.onKeydown.bind(this);
        this.handleProductSubmit = this.onProductSubmit.bind(this);
        this.handleGuideAction = this.onGuideAction.bind(this);
        this.handleGuideDialogClick = this.onGuideDialogClick.bind(this);
        this.handleGuideDialogClose = this.onGuideDialogClose.bind(this);
        this.handleVariantChange = this.onVariantChange.bind(this);

        this.openButton.addEventListener('click', this.handleOpen);
        this.closeButton.addEventListener('click', this.handleClose);
        this.dialog.addEventListener('click', this.handleDialogClick);
        this.dialog.addEventListener('close', this.handleDialogClose);
        this.backButton.addEventListener('click', this.handleBack);
        this.nextButton.addEventListener('click', this.handleNext);
        this.saveButton.addEventListener('click', this.handleSave);
        this.copyButton.addEventListener('click', this.handleCopy);
        this.clearButton.addEventListener('click', this.handleClear);
        this.addEventListener('input', this.handleInput);
        this.shapeInputs.forEach((input) => input.addEventListener('change', this.handleShapeChange));
        this.addEventListener('keydown', this.handleKeydown);
        this.addEventListener('click', this.handleGuideAction);
        this.guideDialog?.addEventListener('click', this.handleGuideDialogClick);
        this.guideDialog?.addEventListener('close', this.handleGuideDialogClose);
        this.variantRoot()?.addEventListener('change', this.handleVariantChange);
        document.addEventListener('submit', this.handleProductSubmit, true);

        this.restore();
        if (!this.shape) this.shape = this.detectSelectedShape();
        this.fillInputs();
        this.updateAllHandDiagrams();
        this.fillShape();
        this.updateSavedState();
        if (this.saved) {
          this.injectProperties();
          this.applyRecommendedVariant();
          window.setTimeout(() => this.injectProperties(), 500);
        }
        this.showStep(0, false);
      }

      disconnectedCallback() {
        this.openButton?.removeEventListener('click', this.handleOpen);
        this.closeButton?.removeEventListener('click', this.handleClose);
        this.dialog?.removeEventListener('click', this.handleDialogClick);
        this.dialog?.removeEventListener('close', this.handleDialogClose);
        this.backButton?.removeEventListener('click', this.handleBack);
        this.nextButton?.removeEventListener('click', this.handleNext);
        this.saveButton?.removeEventListener('click', this.handleSave);
        this.copyButton?.removeEventListener('click', this.handleCopy);
        this.clearButton?.removeEventListener('click', this.handleClear);
        this.removeEventListener('input', this.handleInput);
        this.shapeInputs?.forEach((input) => input.removeEventListener('change', this.handleShapeChange));
        this.removeEventListener('keydown', this.handleKeydown);
        this.removeEventListener('click', this.handleGuideAction);
        this.guideDialog?.removeEventListener('click', this.handleGuideDialogClick);
        this.guideDialog?.removeEventListener('close', this.handleGuideDialogClose);
        this.variantRoot()?.removeEventListener('change', this.handleVariantChange);
        document.removeEventListener('submit', this.handleProductSubmit, true);
      }

      open() {
        if (typeof this.dialog.showModal !== 'function') return;
        this.showStep(this.shape && this.hasCompleteMeasurements() ? 3 : 0, false);
        this.dialog.showModal();
        document.body.classList.add('overflow-hidden');
        window.requestAnimationFrame(() => {
          const heading = this.activeStep()?.querySelector('h3');
          heading?.focus({ preventScroll: true });
        });
      }

      close() {
        if (this.guideDialog?.open) this.guideDialog.close();
        if (this.dialog.open) this.dialog.close();
      }

      onGuideAction(event) {
        const openButton = event.target.closest?.('[data-guide-image-open]');
        if (openButton) {
          this.openGuideImage(Number(openButton.dataset.guideImageOpen), openButton);
          return;
        }

        if (event.target.closest?.('[data-guide-close]')) this.guideDialog?.close();
      }

      openGuideImage(step, trigger) {
        const guide = this.guideViews[step];
        if (!guide || !this.guideDialog || typeof this.guideDialog.showModal !== 'function') return;
        this.guideTrigger = trigger;
        this.guideImage?.setAttribute('viewBox', guide.viewBox);
        const imageSource =
          guide.image === 'size' ? this.guideImageSource?.dataset.sizeSrc : this.guideImageSource?.dataset.overviewSrc;
        if (imageSource) this.guideImageSource?.setAttribute('href', imageSource);
        this.guideImageSource?.setAttribute('width', String(guide.imageWidth));
        this.guideImageSource?.setAttribute('height', String(guide.imageHeight));
        if (this.guideTitle) this.guideTitle.textContent = guide.title;
        if (this.guideCaption) this.guideCaption.textContent = guide.caption;
        this.guideDialog.showModal();
        window.requestAnimationFrame(() => this.guideCloseButton?.focus({ preventScroll: true }));
      }

      onGuideDialogClick(event) {
        if (event.target === this.guideDialog) this.guideDialog.close();
      }

      onGuideDialogClose() {
        this.guideTrigger?.focus({ preventScroll: true });
        this.guideTrigger = null;
      }

      onDialogClose() {
        document.body.classList.remove('overflow-hidden');
        const focusTarget = this.focusAfterClose || this.openButton;
        this.focusAfterClose = null;
        focusTarget?.focus({ preventScroll: true });

        if (focusTarget && focusTarget !== this.openButton) {
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          focusTarget.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
          focusTarget.classList.add('xoa-nail-measurement-cart-ready');
          window.setTimeout(() => focusTarget.classList.remove('xoa-nail-measurement-cart-ready'), 2200);
        }
      }

      onDialogClick(event) {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        const inside =
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom;
        if (!inside) this.close();
      }

      activeStep() {
        return this.querySelector(`[data-measurement-step="${this.currentStep}"]`);
      }

      showStep(step, moveFocus = true) {
        this.currentStep = Math.max(0, Math.min(3, step));
        this.dataset.currentStep = String(this.currentStep);
        this.querySelectorAll('[data-measurement-step]').forEach((panel) => {
          panel.hidden = Number(panel.dataset.measurementStep) !== this.currentStep;
        });

        const progress = (this.currentStep + 1) * 25;
        this.progressLabel.textContent = `Vaihe ${this.currentStep + 1} / 4`;
        this.progressName.textContent = this.stepNames[this.currentStep];
        this.progressTrack.setAttribute('aria-valuenow', String(progress));
        this.progressBar.style.width = `${progress}%`;
        this.backButton.hidden = this.currentStep === 0;
        this.nextButton.hidden = this.currentStep === 3;
        this.saveButton.hidden = this.currentStep !== 3;
        this.copyButton.hidden = this.currentStep !== 3;
        this.clearButton.hidden = this.currentStep !== 3;

        const nextLabels = ['Syötä mitat', 'Seuraava käsi', 'Tarkista mitat'];
        const nextLabel = this.nextButton.querySelector('span');
        if (nextLabel && this.currentStep < 3) nextLabel.textContent = nextLabels[this.currentStep];

        if (this.currentStep === 3) this.renderResults();
        if (this.viewport) this.viewport.scrollTop = 0;

        if (moveFocus) {
          window.requestAnimationFrame(() => {
            const heading = this.activeStep()?.querySelector('h3');
            heading?.focus({ preventScroll: true });
          });
        }
      }

      next() {
        if (this.currentStep === 0 && !this.validateShape()) return;
        if (this.currentStep === 1 && !this.validateHand('left')) return;
        if (this.currentStep === 2 && !this.validateHand('right')) return;
        this.showStep(this.currentStep + 1);
      }

      back() {
        this.showStep(this.currentStep - 1);
      }

      validateShape() {
        if (this.shape && this.charts[this.shape]) {
          if (this.shapeError) this.shapeError.hidden = true;
          return true;
        }
        if (this.shapeError) this.shapeError.hidden = false;
        this.shapeInputs[0]?.focus();
        return false;
      }

      onShapeChange(event) {
        const input = event.currentTarget;
        if (!input.checked || !this.charts[input.value]) return;
        this.shape = input.value;
        this.recommendation = null;
        this.saved = false;
        this.removeProperties();
        if (this.shapeError) this.shapeError.hidden = true;
        this.updateSavedState();
        this.persist();
      }

      fillShape() {
        this.shapeInputs.forEach((input) => {
          input.checked = input.value === this.shape;
        });
      }

      detectSelectedShape() {
        const root = document.getElementById(`variant-selects-${this.dataset.sectionId}`);
        if (!root) return null;
        const selected = Array.from(root.querySelectorAll('input:checked, select'))
          .map((control) => control.value || '')
          .join(' ');
        const variantJson = root.querySelector('[data-selected-variant]')?.textContent || '';
        const value = `${selected} ${variantJson}`.toLocaleLowerCase('fi');
        if (value.includes('long stiletto')) return 'long_stiletto';
        if (value.includes('medium stiletto')) return 'medium_stiletto';
        if (value.includes('long coffin')) return 'long_coffin';
        if (value.includes('medium coffin')) return 'medium_coffin';
        if (value.includes('medium square')) return 'medium_square';
        if (value.includes('medium almond') || value.includes('almond')) return 'medium_almond';
        return null;
      }

      parse(value) {
        const normalized = String(value).trim().replace(',', '.');
        if (!/^\d+(?:\.\d)?$/.test(normalized)) return null;
        const parsed = Number(normalized);
        if (!Number.isFinite(parsed) || parsed < this.minimum || parsed > this.maximum) return null;
        return Math.round(parsed * 10) / 10;
      }

      validationReason(value, required = false) {
        const raw = String(value).trim();
        const range = `${this.format(this.minimum)}–${this.format(this.maximum)} mm`;
        if (!raw) return required ? `Syötä mitta. Sallittu väli on ${range}.` : '';
        if (!/^\d+(?:[.,]\d+)?$/.test(raw)) return 'Käytä vain numeroita sekä yhtä pilkkua tai pistettä.';

        const decimal = raw.split(/[.,]/)[1] || '';
        if (decimal.length > 1) return 'Käytä enintään yhtä desimaalia, esimerkiksi 12,5 mm.';

        const parsed = Number(raw.replace(',', '.'));
        if (!Number.isFinite(parsed)) return 'Syötä mitta numerona, esimerkiksi 12,5 mm.';
        if (parsed < this.minimum) return `Liian pieni arvo. Pienin sallittu mitta on ${this.format(this.minimum)} mm.`;
        if (parsed > this.maximum) return `Liian suuri arvo. Suurin sallittu mitta on ${this.format(this.maximum)} mm.`;
        return '';
      }

      setFieldError(input, message) {
        const error = input.closest('.xoa-nail-measurement__field')?.querySelector('[data-field-error]');
        const isInvalid = Boolean(message);
        input.setAttribute('aria-invalid', String(isInvalid));
        if (!error) return;
        error.textContent = message;
        error.hidden = !isInvalid;
      }

      format(value) {
        return new Intl.NumberFormat('fi-FI', { maximumFractionDigits: 1 }).format(value);
      }

      validateHand(hand) {
        const inputs = this.fingers.map((finger) => this.querySelector(`[data-measurement-input="${hand}.${finger}"]`));
        const invalid = [];

        inputs.forEach((input, index) => {
          const value = this.parse(input.value);
          const reason = this.validationReason(input.value, true);
          this.setFieldError(input, reason);
          if (reason) invalid.push(input);
          else this.values[hand][this.fingers[index]] = value;
        });

        const step = this.querySelector(`[data-measurement-step="${hand === 'left' ? 1 : 2}"]`);
        const error = step?.querySelector('[data-step-error]');
        if (invalid.length) {
          if (error) {
            error.textContent = `Korjaa ${invalid.length === 1 ? 'punaisella merkitty mitta' : 'punaisella merkityt mitat'}. Sallittu väli on ${this.format(this.minimum)}–${this.format(this.maximum)} mm.`;
            error.hidden = false;
          }
          invalid[0].focus();
          return false;
        }

        if (error) error.hidden = true;
        this.persist();
        return true;
      }

      onInput(event) {
        const input = event.target.closest?.('[data-measurement-input]');
        if (!input) return;
        input.value = input.value.replace(/[^0-9.,]/g, '').slice(0, 5);
        this.setFieldError(input, this.validationReason(input.value));
        const [hand, finger] = input.dataset.measurementInput.split('.');
        const value = this.parse(input.value);
        if (value !== null) this.values[hand][finger] = value;
        else delete this.values[hand][finger];
        this.updateHandDiagram(hand, finger, input.value);
        this.recommendation = null;
        this.saved = false;
        this.removeProperties();
        this.updateSavedState();
        this.persist();
      }

      onKeydown(event) {
        const input = event.target.closest?.('[data-measurement-input]');
        if (!input || event.key !== 'Enter') return;
        event.preventDefault();
        const inputs = Array.from(this.activeStep()?.querySelectorAll('[data-measurement-input]') || []);
        const index = inputs.indexOf(input);
        if (index >= 0 && index < inputs.length - 1) inputs[index + 1].focus();
        else this.next();
      }

      hasCompleteMeasurements() {
        return this.hands.every((hand) => this.fingers.every((finger) => this.parse(this.values[hand][finger]) !== null));
      }

      valuesFor(hand) {
        return this.fingers.map((finger) => this.parse(this.values[hand][finger]));
      }

      handText(hand) {
        return this.valuesFor(hand).map((value) => this.format(value)).join(' · ') + ' mm';
      }

      updateHandDiagram(hand, finger, rawValue = '') {
        const group = this.querySelector(`[data-hand-value="${hand}.${finger}"]`);
        const text = this.querySelector(`[data-hand-diagram-value="${hand}.${finger}"]`);
        if (!group || !text) return;
        const cleaned = String(rawValue).trim().replace('.', ',');
        const valid = this.parse(rawValue) !== null;
        text.textContent = cleaned || '–';
        group.classList.toggle('is-filled', Boolean(cleaned));
        group.classList.toggle('is-valid', valid);
      }

      updateAllHandDiagrams() {
        this.hands.forEach((hand) => {
          this.fingers.forEach((finger) => {
            const input = this.querySelector(`[data-measurement-input="${hand}.${finger}"]`);
            this.updateHandDiagram(hand, finger, input?.value || '');
          });
        });
      }

      tipCodeFor(value, fingerIndex) {
        const widths = this.charts[this.shape]?.widths;
        if (!widths) return null;
        const fittingCodes = widths
          .map((width, code) => ({ width, code }))
          .filter((tip) => tip.width >= value)
          .sort((first, second) => first.width - second.width);
        if (!fittingCodes.length) return null;

        const narrowestWidth = fittingCodes[0].width;
        const equallyWide = fittingCodes.filter((tip) => tip.width === narrowestWidth);
        if (equallyWide.length === 1) return equallyWide[0].code;

        const preferredCodes = Object.values(this.sizeSets).map((codes) => codes[fingerIndex]);
        return equallyWide
          .sort(
            (first, second) =>
              preferredCodes.filter((code) => code === second.code).length -
              preferredCodes.filter((code) => code === first.code).length
          )[0].code;
      }

      codesFor(hand) {
        return this.valuesFor(hand).map((value, fingerIndex) => this.tipCodeFor(value, fingerIndex));
      }

      sameCodes(first, second) {
        return first.length === second.length && first.every((value, index) => value === second[index]);
      }

      nextNarrowerWidth(code) {
        const widths = this.charts[this.shape]?.widths || [];
        const current = widths[code];
        for (let nextCode = code + 1; nextCode < widths.length; nextCode += 1) {
          if (widths[nextCode] < current) return widths[nextCode];
        }
        return null;
      }

      sizeFit(size) {
        const codes = this.sizeSets[size];
        const widths = this.charts[this.shape]?.widths;
        if (!codes || !widths) return null;
        const measurements = [...this.valuesFor('left'), ...this.valuesFor('right')];
        let totalSlack = 0;

        const fits = measurements.every((measurement, measurementIndex) => {
          const fingerIndex = measurementIndex % this.fingers.length;
          const code = codes[fingerIndex];
          const width = widths[code];
          const nextNarrower = this.nextNarrowerWidth(code);
          const isInTipRange = measurement <= width && (nextNarrower === null || measurement > nextNarrower);
          if (isInTipRange) totalSlack += width - measurement;
          return isInTipRange;
        });

        return fits ? { size, codes: [...codes], totalSlack } : null;
      }

      calculateRecommendation() {
        const customLeft = this.codesFor('left');
        const customRight = this.codesFor('right');
        const completeCodes = [...customLeft, ...customRight].every((code) => code !== null);
        const preset = Object.keys(this.sizeSets)
          .map((size) => this.sizeFit(size))
          .filter(Boolean)
          .sort((first, second) => first.totalSlack - second.totalSlack)[0];
        const size = preset?.size || 'Custom';
        const left = preset ? [...preset.codes] : customLeft;
        const right = preset ? [...preset.codes] : customRight;

        return {
          size,
          shape: this.shape,
          shapeLabel: this.charts[this.shape]?.label || '',
          left,
          right,
          completeCodes,
        };
      }

      codeText(codes) {
        return codes.map((code) => (code === null ? '–' : code)).join(' · ');
      }

      renderResults() {
        this.recommendation = this.calculateRecommendation();
        const isCustom = this.recommendation.size === 'Custom';

        if (this.recommendedSize) this.recommendedSize.textContent = this.recommendation.size;
        if (this.recommendedShape) this.recommendedShape.textContent = this.recommendation.shapeLabel;
        if (this.recommendationCopy) {
          this.recommendationCopy.textContent = isCustom
            ? 'Mittaustuloksesi ei vastaa yhtä valmis­kokoa molemmissa käsissä. Custom-koko antaa jokaiselle kynnelle oikean tipin.'
            : `Molemmat kädet vastaavat XOA:n ${this.recommendation.size}-kokoa.`;
        }
        if (this.recommendationNote) {
          this.recommendationNote.textContent = isCustom
            ? 'Custom-tilaukseen tallennetaan sekä millimetrimitat että tippinumerot molemmista käsistä. Erillistä ostoskoriviestiä ei tarvita.'
            : `Valmis ${this.recommendation.size}-setti sisältää tippinumerot ${this.codeText(this.sizeSets[this.recommendation.size])}.`;
        }
        if (this.cartFlowSaveTitle) {
          this.cartFlowSaveTitle.textContent = isCustom
            ? 'Tallenna Custom-mitat tuotteelle'
            : `Valitse koko ${this.recommendation.size} tuotteelle`;
        }
        if (this.cartFlowSaveCopy) {
          this.cartFlowSaveCopy.textContent = isCustom
            ? 'Työkalu valitsee Custom-koon ja liittää mitat sekä tippinumerot tuotteeseen. Erillistä viestiä ei tarvita.'
            : `Työkalu valitsee tuotteelta koon ${this.recommendation.size} ja liittää kokosuosituksen.`;
        }
        if (this.cartFlowCartCopy) {
          this.cartFlowCartCopy.textContent = isCustom
            ? 'Paina sen jälkeen Lisää ostoskoriin. Mitat ja tippinumerot näkyvät tuoterivillä.'
            : 'Paina sen jälkeen Lisää ostoskoriin. Kokosuositus näkyy tuoterivillä.';
        }
        if (this.saveButton) {
          this.saveButton.textContent = isCustom
            ? 'Tallenna Custom-mitat tuotteelle'
            : `Valitse koko ${this.recommendation.size} tuotteelle`;
        }

        this.hands.forEach((hand) => {
          const codes = this.querySelector(`[data-result-codes="${hand}"]`);
          const measurements = this.querySelector(`[data-result-measurements="${hand}"]`);
          if (codes) codes.textContent = this.codeText(this.recommendation[hand]);
          if (measurements) measurements.textContent = this.handText(hand);
        });
        if (this.announcement) this.announcement.textContent = '';
      }

      measurementText() {
        const recommendation = this.recommendation || this.calculateRecommendation();
        return [
          `${this.dataset.productTitle || 'Kynnet'} – press-on-koko ${recommendation.size}`,
          `Malli: ${recommendation.shapeLabel}`,
          `Vasen, tippinumerot (P–E–K–N–Pk): ${this.codeText(recommendation.left)}`,
          `Oikea, tippinumerot (P–E–K–N–Pk): ${this.codeText(recommendation.right)}`,
          `Vasen (P–E–K–N–Pk): ${this.handText('left')}`,
          `Oikea (P–E–K–N–Pk): ${this.handText('right')}`,
        ].join('\n');
      }

      save() {
        if (!this.shape) {
          this.showStep(0);
          return;
        }
        if (!this.hasCompleteMeasurements()) {
          this.showStep(1);
          return;
        }
        this.recommendation = this.calculateRecommendation();
        this.saved = true;
        this.persist();
        this.injectProperties();
        const selectedVariant = this.applyRecommendedVariant();
        this.updateSavedState();
        if (this.announcement) {
          const isCustom = this.recommendation.size === 'Custom';
          this.announcement.textContent = selectedVariant
            ? isCustom
              ? 'Custom-koko ja mitat tallennettiin. Seuraavaksi lisää tuote ostoskoriin.'
              : `Koko ${this.recommendation.size} valittiin. Seuraavaksi lisää tuote ostoskoriin.`
            : `Kokosuositus ${this.recommendation.size} tallennettiin. Tarkista vielä tuotteen kokovalinta.`;
        }
        window.setTimeout(() => {
          this.injectProperties();
          this.focusAfterClose = this.productAddButton();
          this.close();
        }, 700);
      }

      productForm() {
        return document.getElementById(`product-form-${this.dataset.sectionId}`);
      }

      productAddButton() {
        return this.productForm()?.querySelector('[name="add"][type="submit"]') || null;
      }

      variantRoot() {
        return document.getElementById(`variant-selects-${this.dataset.sectionId}`);
      }

      injectProperties() {
        const form = this.productForm();
        if (!form || !this.shape || !this.hasCompleteMeasurements()) return;

        this.recommendation = this.recommendation || this.calculateRecommendation();

        form.querySelectorAll('[data-xoa-nail-property]').forEach((input) => input.remove());
        const isCustom = this.recommendation.size === 'Custom';
        const properties = { 'Press-on-koko': `${this.recommendation.size} · ${this.recommendation.shapeLabel}` };

        if (isCustom) {
          properties['Vasen mitat (P–E–K–N–Pk)'] = this.handText('left');
          properties['Oikea mitat (P–E–K–N–Pk)'] = this.handText('right');
          properties['Vasen tippinumerot (P–E–K–N–Pk)'] = this.codeText(this.recommendation.left);
          properties['Oikea tippinumerot (P–E–K–N–Pk)'] = this.codeText(this.recommendation.right);
        } else {
          properties['_Vasen mitta (P–E–K–N–Pk)'] = this.handText('left');
          properties['_Oikea mitta (P–E–K–N–Pk)'] = this.handText('right');
        }

        Object.entries(properties).forEach(([name, value]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${name}]`;
          input.value = value;
          input.dataset.xoaNailProperty = '';
          form.append(input);
        });
      }

      normalizeVariantValue(value) {
        return String(value).toLocaleLowerCase('fi').replace(/[^a-z0-9åäö]/g, '');
      }

      sizeAliases(size) {
        const aliases = {
          XS: ['xs', 'xsmall', 'extrasmall'],
          S: ['s', 'small'],
          M: ['m', 'medium'],
          L: ['l', 'large'],
          XL: ['xl', 'xlarge', 'extralarge'],
          Custom: ['custom', 'customkoko', 'mittatilaus'],
        };
        return aliases[size] || [];
      }

      applyRecommendedVariant() {
        const root = this.variantRoot();
        if (!root || !this.recommendation) return false;

        const desiredGroups = [
          this.sizeAliases(this.recommendation.size),
          this.charts[this.recommendation.shape]?.variantAliases || [this.normalizeVariantValue(this.recommendation.shapeLabel)],
        ];
        const changed = [];

        desiredGroups.forEach((aliases) => {
          const controls = Array.from(root.querySelectorAll('input[type="radio"], select'));
          const control = controls.find((candidate) => {
            if (candidate.matches('input.disabled, input:disabled, select:disabled')) return false;
            if (candidate.tagName === 'SELECT') {
              return Array.from(candidate.options).some((option) => aliases.includes(this.normalizeVariantValue(option.value)));
            }
            return aliases.includes(this.normalizeVariantValue(candidate.value));
          });

          if (!control) return;
          if (control.tagName === 'SELECT') {
            const option = Array.from(control.options).find((item) => aliases.includes(this.normalizeVariantValue(item.value)));
            if (option && control.value !== option.value) {
              control.value = option.value;
              changed.push(control);
            }
          } else if (!control.checked) {
            control.checked = true;
            changed.push(control);
          }
        });

        if (!changed.length) {
          const selectedValues = Array.from(root.querySelectorAll('input:checked, select')).map((control) =>
            this.normalizeVariantValue(control.value)
          );
          return this.sizeAliases(this.recommendation.size).some((alias) => selectedValues.includes(alias));
        }

        changed[changed.length - 1].dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }

      onProductSubmit(event) {
        if (!this.saved || event.target.id !== `product-form-${this.dataset.sectionId}`) return;
        this.injectProperties();
      }

      selectedVariantMatchesRecommendation() {
        const root = this.variantRoot();
        if (!root || !this.recommendation) return false;
        const selectedValues = Array.from(root.querySelectorAll('input:checked, select')).map((control) =>
          this.normalizeVariantValue(control.value)
        );
        const sizeMatches = this.sizeAliases(this.recommendation.size).some((alias) => selectedValues.includes(alias));
        const shapeAliases = this.charts[this.recommendation.shape]?.variantAliases || [];
        const shapeMatches = shapeAliases.some((alias) => selectedValues.includes(alias));
        return sizeMatches && shapeMatches;
      }

      onVariantChange() {
        if (!this.saved || this.selectedVariantMatchesRecommendation()) return;
        this.saved = false;
        this.recommendation = null;
        this.removeProperties();
        this.persist();
        this.updateSavedState();
      }

      async copy() {
        const text = this.measurementText();
        try {
          await navigator.clipboard.writeText(text);
        } catch (error) {
          const textarea = document.createElement('textarea');
          textarea.value = text;
          textarea.setAttribute('readonly', '');
          textarea.style.position = 'fixed';
          textarea.style.opacity = '0';
          document.body.append(textarea);
          textarea.select();
          document.execCommand('copy');
          textarea.remove();
        }
        if (this.announcement) this.announcement.textContent = 'Mitat kopioitiin leikepöydälle.';
      }

      clear() {
        this.values = { left: {}, right: {} };
        this.recommendation = null;
        this.saved = false;
        this.querySelectorAll('[data-measurement-input]').forEach((input) => {
          input.value = '';
          this.setFieldError(input, '');
        });
        this.updateAllHandDiagrams();
        this.removeProperties();
        this.persist();
        this.updateSavedState();
        this.showStep(1);
      }

      removeProperties() {
        const form = this.productForm();
        form?.querySelectorAll('[data-xoa-nail-property]').forEach((input) => input.remove());
      }

      updateSavedState() {
        if (this.savedMessage) this.savedMessage.hidden = !this.saved;
        if (this.saved && this.savedMessage) {
          const recommendation = this.recommendation || this.calculateRecommendation();
          const isCustom = recommendation.size === 'Custom';
          if (this.savedTitle) {
            this.savedTitle.textContent = isCustom
              ? 'Custom-mitat tallennettu tälle tuotteelle'
              : `Koko ${recommendation.size} tallennettu tälle tuotteelle`;
          }
          if (this.savedHelp) {
            this.savedHelp.textContent = isCustom
              ? 'Lisää tuote ostoskoriin – mitat ja tippinumerot siirtyvät tuoteriville automaattisesti.'
              : 'Lisää tuote ostoskoriin – kokosuositus siirtyy tuoteriville.';
          }
        }
        const openLabel = this.querySelector('[data-open-label]');
        if (openLabel) openLabel.textContent = this.saved ? 'Tarkista kokosuositus' : 'Aloita mittaus';
      }

      fillInputs() {
        this.querySelectorAll('[data-measurement-input]').forEach((input) => {
          const [hand, finger] = input.dataset.measurementInput.split('.');
          const value = this.parse(this.values[hand]?.[finger]);
          input.value = value === null ? '' : this.format(value);
        });
      }

      persist() {
        try {
          localStorage.setItem(this.storageKey, JSON.stringify({ values: this.values, shape: this.shape, saved: this.saved }));
        } catch (error) {
          // The tool remains fully usable when browser storage is unavailable.
        }
      }

      restore() {
        try {
          const stored = JSON.parse(localStorage.getItem(this.storageKey));
          if (!stored || typeof stored !== 'object') return;
          const storedShape = this.legacyShapes[stored.shape] || stored.shape;
          if (storedShape && this.charts[storedShape]) this.shape = storedShape;
          this.hands.forEach((hand) => {
            this.fingers.forEach((finger) => {
              const value = this.parse(stored.values?.[hand]?.[finger]);
              if (value !== null) this.values[hand][finger] = value;
            });
          });
          this.saved = Boolean(stored.saved) && Boolean(this.shape) && this.hasCompleteMeasurements();
          if (this.saved) this.recommendation = this.calculateRecommendation();
        } catch (error) {
          this.values = { left: {}, right: {} };
          this.shape = null;
          this.recommendation = null;
          this.saved = false;
        }
      }
    }
  );
}
